import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Mail, Send, Download, CheckCircle2, Clock, FileText, MessageSquare, Ban, CalendarClock, Undo2 } from "lucide-react";
import AppShell from "../components/app/AppShell";
import PageHeader, { StatusPill } from "../components/app/PageHeader";
import api, { formatApiError } from "../lib/api";
import { track, EVT } from "../lib/analytics";
import { useAuth } from "../context/AuthContext";

export default function ClientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canManage = ["firm_admin", "compliance_manager"].includes(user?.role);
  const [client, setClient] = useState(null);
  const [reminding, setReminding] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [expiryOpen, setExpiryOpen] = useState(false);
  const [newExpiry, setNewExpiry] = useState("");
  const [savingExpiry, setSavingExpiry] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/api/clients/${id}`); setClient(data); setNewExpiry((data.expires_at || "").slice(0, 10)); }
    catch (e) { alert(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [id]);

  const sendReminder = async () => {
    setReminding(true);
    try {
      await api.post(`/api/clients/${id}/remind`, {});
      await load();
      alert("Reminder sent.");
    } catch (e) { alert(formatApiError(e)); }
    finally { setReminding(false); }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke this client's portal access? They will see the branded 'revoked' screen.")) return;
    setRevoking(true);
    try { await api.patch(`/api/clients/${id}`, { action: "revoke" }); await load(); }
    catch (e) { alert(formatApiError(e)); }
    finally { setRevoking(false); }
  };
  const unrevoke = async () => {
    setRevoking(true);
    try { await api.patch(`/api/clients/${id}`, { action: "unrevoke" }); await load(); }
    catch (e) { alert(formatApiError(e)); }
    finally { setRevoking(false); }
  };
  const saveExpiry = async () => {
    setSavingExpiry(true);
    try {
      const iso = newExpiry ? new Date(newExpiry + "T23:59:59Z").toISOString() : null;
      await api.patch(`/api/clients/${id}`, { expires_at: iso });
      setExpiryOpen(false); await load();
    } catch (e) { alert(formatApiError(e)); }
    finally { setSavingExpiry(false); }
  };

  if (!client) return <AppShell><div className="flex justify-center py-20"><Loader2 size={32} className="animate-spin text-indigo2" /></div></AppShell>;
  const isRevoked = client.status === "archived";

  const completed = (client.progress?.completed_steps || []).length;
  const total = client.flow?.steps?.length || 1;
  const pct = Math.round((completed / total) * 100);

  return (
    <AppShell>
      <div className="mb-4"><Link to="/app/clients" className="btn btn-ghost btn-sm"><ArrowLeft size={16} />All clients</Link></div>
      <PageHeader
        title={`${client.first_name} ${client.last_name}`}
        subtitle={`${client.email_masked} · Flow: ${client.flow_name} · Sent by ${client.sent_by_name}`}
        actions={
          <div className="flex flex-wrap gap-2">
            <button className="btn btn-secondary" onClick={sendReminder} disabled={reminding || client.status === "complete" || isRevoked} data-testid="client-remind">
              {reminding ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}Send reminder
            </button>
            {canManage && (
              <button className="btn btn-secondary" onClick={() => setExpiryOpen(true)} data-testid="client-expiry"><CalendarClock size={14} />Set expiry</button>
            )}
            {canManage && (
              isRevoked
                ? <button onClick={unrevoke} disabled={revoking} className="btn btn-secondary" data-testid="client-unrevoke">{revoking ? <Loader2 size={14} className="animate-spin" /> : <Undo2 size={14} />}Restore access</button>
                : <button onClick={revoke} disabled={revoking} className="btn btn-danger" data-testid="client-revoke">{revoking ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}Archive client</button>
            )}
            {canManage && (
              <button onClick={async () => {
                try {
                  const base = process.env.REACT_APP_BACKEND_URL;
                  const res = await fetch(`${base}/api/clients/${id}/onboarding-pack`, { credentials: "include" });
                  if (!res.ok) throw new Error("Download failed");
                  const blob = await res.blob();
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  a.download = res.headers.get("Content-Disposition")?.match(/filename="([^"]+)"/)?.[1] || "onboarding-pack.pdf";
                  a.click(); URL.revokeObjectURL(a.href);
                  track(EVT.onboarding_pack_downloaded, { client_id: id });
                } catch (e) { alert("Download failed: " + e.message); }
              }} className="btn btn-primary" data-testid="client-pack">
                <Download size={14} />Download pack
              </button>
            )}
            {client.status === "complete" && false && <button className="btn btn-primary"><Download size={14} />Download pack</button>}
          </div>
        }
      />

      {isRevoked && (
        <div className="card2 p-4 mb-5 border-l-4 border-rose2 bg-rose2-50/30 flex items-center gap-3" data-testid="revoked-banner">
          <Ban size={16} className="text-rose2 flex-shrink-0" />
          <div className="t-body-sm">This client's portal access has been revoked. They will see a branded 'invitation no longer active' screen if they open the link.</div>
        </div>
      )}

      <div className="card2 p-6 mb-6" data-testid="client-progress">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <StatusPill status={client.status} />
            <span className="t-caption">{completed} of {total} steps · {pct}%</span>
          </div>
          <span className="t-caption">Expires {client.expires_at ? new Date(client.expires_at).toLocaleDateString() : "—"}</span>
        </div>
        <div className="h-2 bg-border2 rounded-full overflow-hidden">
          <div className="h-full bg-indigo2 transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-6">
        <div className="space-y-6">
          <div className="card2">
            <div className="px-5 py-4 border-b border-border2"><h3 className="t-h3">Steps</h3></div>
            <div className="divide-y divide-border2">
              {(client.flow?.steps || []).map((s, idx) => {
                const isDone = (client.progress?.completed_steps || []).includes(s.id);
                const isCurrent = idx === (client.progress?.current_step || 0);
                return (
                  <div key={s.id} className="px-5 py-4 flex items-start gap-3" data-testid={`step-detail-${idx}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isDone ? "bg-emerald2-50 text-emerald2-dark" : isCurrent ? "bg-indigo2-50 text-indigo2" : "bg-surface text-muted2"}`}>
                      {isDone ? <CheckCircle2 size={16} /> : <span className="t-mono t-caption">{idx + 1}</span>}
                    </div>
                    <div className="flex-1">
                      <div className="t-body font-medium">{s.name}</div>
                      <div className="t-caption uppercase tracking-wider">{s.type.replace("_", " ")}</div>
                    </div>
                    {isDone && <span className="badge2 badge-verified">Complete</span>}
                    {isCurrent && !isDone && <span className="badge2 badge-pending">In progress</span>}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card2">
            <div className="px-5 py-4 border-b border-border2 flex items-center gap-2"><MessageSquare size={16} className="text-indigo2" /><h3 className="t-h3">Compliance notes</h3></div>
            <div className="p-5">
              {(client.compliance_notes || []).length === 0 ? (
                <p className="t-body-sm text-slate2">No notes yet. Compliance Manager + Admin can add internal notes here.</p>
              ) : (
                <div className="space-y-3">
                  {client.compliance_notes.map((n, i) => (
                    <div key={i} className="p-3 bg-surface rounded-md">
                      <div className="t-caption mb-1">{n.author} · {new Date(n.at).toLocaleString()}</div>
                      <div className="t-body-sm">{n.content}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card2 h-fit" data-testid="client-timeline">
          <div className="px-5 py-4 border-b border-border2 flex items-center gap-2"><Clock size={16} className="text-indigo2" /><h3 className="t-h3">Timeline</h3></div>
          <ol className="p-5 space-y-4">
            {(client.timeline || []).slice().reverse().map((t, i) => (
              <li key={i} className="flex gap-3">
                <div className="w-2 h-2 rounded-full bg-indigo2 mt-2 flex-shrink-0" />
                <div className="flex-1">
                  <div className="t-body-sm">{t.event}</div>
                  <div className="t-caption">{new Date(t.at).toLocaleString()}{t.actor && t.actor !== "system" && ` · ${t.actor}`}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {expiryOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={() => setExpiryOpen(false)}>
          <div className="card2 p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="expiry-modal">
            <h3 className="t-h3 mb-2 flex items-center gap-2"><CalendarClock size={18} className="text-indigo2" />Set portal expiry</h3>
            <p className="t-body-sm text-slate2 mb-3">After this date the client will see a branded 'invitation has expired' screen.</p>
            <input type="date" className="input2" value={newExpiry} onChange={(e) => setNewExpiry(e.target.value)} data-testid="expiry-input" />
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setExpiryOpen(false)} className="btn btn-secondary">Cancel</button>
              <button onClick={saveExpiry} disabled={savingExpiry} className="btn btn-primary" data-testid="expiry-save">{savingExpiry ? <Loader2 size={14} className="animate-spin" /> : null}Save expiry</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
